import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CImage,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react'
import { useNavigate } from 'react-router-dom'
import { SplashScreen } from '@capacitor/splash-screen'
import { Capacitor } from '@capacitor/core'
import apiClient from '../../services/apiClient'
import { buildApiUrl } from '../../utils/getConfiguration'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'
import CIcon from '@coreui/icons-react'
import { cibApple, cibGooglePay, cilCreditCard } from '@coreui/icons'
import { PAYWALL_RESULT, Purchases } from '@revenuecat/purchases-capacitor'
import { RevenueCatUI } from '@revenuecat/purchases-capacitor-ui'
import {
  applyRevenueCatEntitlements,
  ensureRevenueCatConfigured,
  getRevenueCatCustomerInfo,
  getRevenueCatUserId,
  isRevenueCatAvailable,
} from '../../utils/revenueCat'

const readMembershipFromStorage = () => {
  if (typeof window === 'undefined') {
    return ''
  }
  return String(window.localStorage.getItem('membership') || '')
}

const normalizeMembershipName = (value) => {
  if (!value && value !== 0) {
    return ''
  }
  return String(value).trim().toUpperCase()
}

const DEFAULT_ENTITLEMENT = 'pro'
const MEMBERSHIP_PRODUCT_ID_MAP = Object.freeze({
  PRO: 'reminderai.pro.yearly',
})
const MEMBERSHIP_ENTITLEMENT_MAP = Object.freeze({
  PRO: 'pro',
})
const MEMBERSHIP_OFFERING_MAP = Object.freeze({
  PRO: 'default',
})
const MEMBERSHIP_PACKAGE_MAP = Object.freeze({
  PRO: 'annual',
})
const WEB_STRIPE_PAYMENT_URL = 'https://buy.stripe.com/cNi4gAa8H6qB4On70d5Rm00'

const isPaywallUiAvailable = () =>
  Boolean(
    RevenueCatUI &&
      typeof RevenueCatUI.presentPaywall === 'function' &&
      typeof RevenueCatUI.presentPaywallIfNeeded === 'function',
  )

const shouldMarkPaywallSuccess = (paywallResult) =>
  paywallResult === PAYWALL_RESULT.PURCHASED || paywallResult === PAYWALL_RESULT.RESTORED

const shouldMarkPaywallFailure = (paywallResult) => {
  return (
    paywallResult === PAYWALL_RESULT.NOT_PRESENTED ||
    paywallResult === PAYWALL_RESULT.ERROR ||
    paywallResult === PAYWALL_RESULT.CANCELLED
  )
}

const resolvePaywallResultValue = (response) => {
  if (!response) {
    return undefined
  }
  if (typeof response === 'object' && 'result' in response) {
    return response.result
  }
  return response
}

const logPaymentDebug = (phase, context = {}) => {
  if (typeof console === 'undefined' || typeof console.log !== 'function') {
    return
  }
  console.log('[Payments]', phase, context)
}

const hideSplashScreen = () => {
  if (typeof window === 'undefined') {
    return
  }

  const splash = document.getElementById('splash-screen')
  if (splash) {
    splash.style.display = 'none'
  }

  if (typeof SplashScreen?.hide === 'function') {
    SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
      console.warn('Unable to hide splash screen', splashError)
    })
    return
  }

  if (typeof window.SplashScreen?.hide === 'function') {
    window.SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
      console.warn('Unable to hide splash screen', splashError)
    })
  }
}

const Payments = () => {
  const [accounts, setAccounts] = useState([])
  const [memberships, setMemberships] = useState([])
  const [detailMembership, setDetailMembership] = useState(null)
  const [currentMembership, setCurrentMembership] = useState(() => readMembershipFromStorage())
  const [purchaseAlert, setPurchaseAlert] = useState(null)
  const [purchaseInProgress, setPurchaseInProgress] = useState(null)
  const [revenueCatReady, setRevenueCatReady] = useState(false)
  const [revenueCatError, setRevenueCatError] = useState('')
  const [paywallSupported, setPaywallSupported] = useState(false)
  const { t } = useTranslation()
  const navigate = useNavigate()

  const fetchAccountsData = async () => {
    try {
      const mail = localStorage.getItem('mail')
      const response = await apiClient.get(buildApiUrl('account', { email: mail }))
      setAccounts(response.data)
    } catch (error) {
      console.error('Error fetching accounts', error)
    }
  }

  useEffect(() => {
    fetchAccountsData()
  }, [])

  const fetchMembershipsData = async () => {
    try {
      const response = await apiClient.get(buildApiUrl('memberships'))
      setMemberships(response.data)
    } catch (error) {
      console.error('Error fetching memberships', error)
    }
  }

  useEffect(() => {
    fetchMembershipsData()
    hideSplashScreen()
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const handleMembershipChange = () => {
      setCurrentMembership(readMembershipFromStorage())
    }

    const handleStorage = (event) => {
      if (event?.key && !['membership', 'membershipType'].includes(event.key)) {
        return
      }
      handleMembershipChange()
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener('app:membership-updated', handleMembershipChange)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('app:membership-updated', handleMembershipChange)
    }
  }, [])

  const descriptionClampStyle = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  }

  const detectedPlatform = useMemo(() => {
    try {
      const platform = Capacitor?.getPlatform?.() ?? 'web'
      return platform || 'web'
    } catch (error) {
      console.warn('Unable to detect platform for payments', error)
      return 'web'
    }
  }, [])

  const handleMembershipCancellation = useCallback(
    (source = 'paywall') => {
      const fallbackMembership = 'FREE'
      setCurrentMembership(fallbackMembership)
      persistMembershipInStorage(fallbackMembership)
      updateAccountMembership(fallbackMembership)
      logPaymentDebug('membership:cancelled', {
        source,
        platform: detectedPlatform,
      })
      console.log('[Payments] membership:update', {
        source: `${source}-cancelled`,
        membership: fallbackMembership,
      })
    },
    [detectedPlatform],
  )

  const isApplePaySupported = detectedPlatform === 'ios'
  const isGooglePaySupported = detectedPlatform === 'android'
  const isWebPaySupported = detectedPlatform === 'web'
  const normalizedCurrentMembership = useMemo(
    () => normalizeMembershipName(currentMembership),
    [currentMembership],
  )

  useEffect(() => {
    const hasNativePurchases = isApplePaySupported || isGooglePaySupported
    const hasPaywallUi = isPaywallUiAvailable()

    if (!hasNativePurchases && !hasPaywallUi) {
      logPaymentDebug('revenuecat:skipped', {
        reason: 'no-supported-flow',
        platform: detectedPlatform,
      })
      setRevenueCatReady(false)
      setRevenueCatError('')
      return undefined
    }

    if (!isRevenueCatAvailable()) {
      logPaymentDebug('revenuecat:skipped', {
        reason: 'sdk-unavailable',
        platform: detectedPlatform,
      })
      setRevenueCatReady(false)
      setRevenueCatError('')
      return undefined
    }

    let isActive = true

    const setupRevenueCat = async () => {
      const appUserId = getRevenueCatUserId()
      logPaymentDebug('revenuecat:configure:start', {
        platform: detectedPlatform,
        appUserId,
      })
      const configured = await ensureRevenueCatConfigured({ appUserId })
      if (!isActive) {
        return
      }

      logPaymentDebug('revenuecat:configure:result', {
        platform: detectedPlatform,
        configured,
      })
      setRevenueCatReady(configured)

      if (!configured) {
        setRevenueCatError('')
        logPaymentDebug('revenuecat:configure:failed', {
          platform: detectedPlatform,
          reason: 'not-configured',
        })
        return
      }

      setRevenueCatError('')
      const info = await getRevenueCatCustomerInfo()
      logPaymentDebug('revenuecat:customer-info', {
        platform: detectedPlatform,
        hasInfo: Boolean(info),
      })
      if (!isActive || !info) {
        return
      }
      const membershipName = applyRevenueCatEntitlements(info)
      if (membershipName) {
        logPaymentDebug('membership:update', {
          source: 'revenuecat',
          membershipName,
          platform: detectedPlatform,
        })
        setCurrentMembership(membershipName)
      }
    }

    setupRevenueCat()

    return () => {
      isActive = false
    }
  }, [detectedPlatform, isApplePaySupported, isGooglePaySupported, t])

  useEffect(() => {
    setPaywallSupported(isPaywallUiAvailable() && revenueCatReady)
  }, [revenueCatReady, detectedPlatform])

  const resolveProductIdentifier = (membership) => {
    if (!membership || typeof membership !== 'object') {
      return ''
    }

    const candidateKeys = [
      'revenuecatProductId',
      'revenueCatProductId',
      'revenuecat_product_id',
      'productIdentifier',
      'product_id',
      'sku',
      'membership_name',
      'name',
    ]

    for (const key of candidateKeys) {
      const value = membership[key]
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim()
      }
    }

    const normalizedMembershipName = normalizeMembershipName(
      membership.membership_name || membership.name,
    )
    if (normalizedMembershipName && MEMBERSHIP_PRODUCT_ID_MAP[normalizedMembershipName]) {
      return MEMBERSHIP_PRODUCT_ID_MAP[normalizedMembershipName]
    }

    return ''
  }

  const resolveEntitlementIdentifier = (membership) => {
    if (!membership) {
      return DEFAULT_ENTITLEMENT
    }
    const candidateKeys = [
      'revenuecatEntitlement',
      'revenueCatEntitlement',
      'revenuecat_entitlement',
      'entitlementIdentifier',
      'entitlement_id',
      'entitlement',
    ]
    for (const key of candidateKeys) {
      const value = membership[key]
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim()
      }
    }

    const normalizedMembershipName = normalizeMembershipName(
      membership.membership_name || membership.name,
    )
    if (normalizedMembershipName && MEMBERSHIP_ENTITLEMENT_MAP[normalizedMembershipName]) {
      return MEMBERSHIP_ENTITLEMENT_MAP[normalizedMembershipName]
    }

    return DEFAULT_ENTITLEMENT
  }

  const fetchOfferingByIdentifier = async (identifier) => {
    if (!identifier || typeof Purchases?.getOfferings !== 'function') {
      return undefined
    }
    try {
      const offerings = await Purchases.getOfferings()
      return offerings?.all?.[identifier] || offerings?.current
    } catch (error) {
      console.warn('Unable to load RevenueCat offering', error)
      return undefined
    }
  }

  const resolveOfferingIdentifier = (membership) => {
    if (!membership) {
      return ''
    }
    if (typeof membership?.revenuecatOfferingId === 'string') {
      return membership.revenuecatOfferingId
    }
    if (typeof membership?.revenueCatOfferingId === 'string') {
      return membership.revenueCatOfferingId
    }
    if (typeof membership?.offeringIdentifier === 'string') {
      return membership.offeringIdentifier
    }
    if (typeof membership?.offering_id === 'string') {
      return membership.offering_id
    }
    const normalizedName = normalizeMembershipName(membership.membership_name || membership.name)
    if (normalizedName && MEMBERSHIP_OFFERING_MAP[normalizedName]) {
      return MEMBERSHIP_OFFERING_MAP[normalizedName]
    }
    return ''
  }

  const resolvePackageIdentifier = (membership) => {
    if (!membership) {
      return ''
    }
    const candidateKeys = [
      'revenuecatPackageId',
      'revenueCatPackageId',
      'revenuecat_package_id',
      'packageIdentifier',
      'package_id',
      'package',
    ]
    for (const key of candidateKeys) {
      const value = membership[key]
      if (typeof value === 'string' && value.trim()) {
        return value.trim()
      }
    }
    const normalizedName = normalizeMembershipName(membership.membership_name || membership.name)
    if (normalizedName && MEMBERSHIP_PACKAGE_MAP[normalizedName]) {
      return MEMBERSHIP_PACKAGE_MAP[normalizedName]
    }
    return ''
  }

  const selectPackageFromOffering = (offering, { packageIdentifier, productIdentifier }) => {
    if (!offering || !Array.isArray(offering.availablePackages)) {
      return undefined
    }

    if (packageIdentifier) {
      const match = offering.availablePackages.find(
        (pkg) => pkg?.identifier && pkg.identifier === packageIdentifier,
      )
      if (match) {
        return match
      }
    }

    if (productIdentifier) {
      return offering.availablePackages.find((pkg) => {
        const pkgProductId = pkg?.product?.identifier || pkg?.storeProduct?.identifier
        return pkgProductId === productIdentifier
      })
    }

    return undefined
  }

  const findRevenueCatPackage = async ({
    offeringIdentifier,
    packageIdentifier,
    productIdentifier,
  }) => {
    if (typeof Purchases?.getOfferings !== 'function') {
      return undefined
    }

    try {
      const offerings = await Purchases.getOfferings()
      if (!offerings) {
        return undefined
      }

      const offeringCandidates = []
      if (offeringIdentifier && offerings.all?.[offeringIdentifier]) {
        offeringCandidates.push(offerings.all[offeringIdentifier])
      }
      if (offerings.current) {
        offeringCandidates.push(offerings.current)
      }
      offeringCandidates.push(...Object.values(offerings.all || {}))

      for (const offering of offeringCandidates) {
        const pkg = selectPackageFromOffering(offering, { packageIdentifier, productIdentifier })
        if (pkg) {
          return pkg
        }
      }
    } catch (error) {
      console.warn('Unable to locate RevenueCat package', error)
    }

    return undefined
  }

  const purchaseMembershipWithRevenueCat = async ({
    productIdentifier,
    offeringIdentifier,
    packageIdentifier,
  }) => {
    const revenuecat = Purchases
    if (!revenuecat) {
      throw new Error('RevenueCat SDK is not available')
    }

    const targetPackage = await findRevenueCatPackage({
      offeringIdentifier,
      packageIdentifier,
      productIdentifier,
    })

    if (targetPackage && typeof revenuecat.purchasePackage === 'function') {
      return revenuecat.purchasePackage({ aPackage: targetPackage })
    }

    if (typeof revenuecat.purchaseStoreProduct === 'function') {
      return revenuecat.purchaseStoreProduct({ productIdentifier })
    }

    throw new Error('RevenueCat purchase functions are unavailable on this platform')
  }

  const initiateCheckout = (membership, method) => {
    if (!membership) {
      return
    }

    const membershipName = membership.membership_name || membership.name || 'PRO'
    const price = membership.price || '0'

    try {
      localStorage.setItem('newmembership', membershipName)
      localStorage.setItem('newprice', price)
      localStorage.setItem('paymentMethod', method)
    } catch (error) {
      console.warn('Unable to persist checkout data', error)
    }

    navigate('/payments')
  }

  const persistMembershipInStorage = (value) => {
    if (typeof window === 'undefined' || !value) {
      return
    }
    try {
      window.localStorage.setItem('membership', value)
      window.localStorage.setItem('membershipType', value)
      window.dispatchEvent(new Event('app:membership-updated'))
    } catch (error) {
      console.warn('Unable to persist membership in storage', error)
    }
  }

  const updateAccountMembership = async (membershipName) => {
    if (typeof window === 'undefined' || !membershipName) {
      return
    }
    const storage = window.localStorage
    const accountId = storage.getItem('account_ID') || storage.getItem('account_id')
    if (!accountId) {
      return
    }
    const normalizedMembership = normalizeMembershipName(membershipName) || membershipName
    try {
      await apiClient.get(
        buildApiUrl('accountMembershipUpdate', {
          account_ID: accountId,
          membership: normalizedMembership,
        }),
      )
      console.log('[Payments] accountMembershipUpdate success', {
        accountId,
        membership: normalizedMembership,
      })
    } catch (error) {
      console.warn('Unable to update account membership', error)
    }
  }

  const getPaywallButtonContent = (membership) => {
    const planName = membership?.name || membership?.membership_name || 'PRO'
    if (detectedPlatform === 'ios') {
      return {
        label: t('payments.buttons.applePay'),
        ariaLabel: t('payments.buttons.applePayAria', { plan: planName }),
        icon: cibApple,
      }
    }
    if (detectedPlatform === 'android') {
      return {
        label: t('payments.buttons.googlePay'),
        ariaLabel: t('payments.buttons.googlePayAria', { plan: planName }),
        icon: cibGooglePay,
      }
    }
    return {
      label: t('payments.buttons.webPay'),
      ariaLabel: t('payments.buttons.webPayAria', { plan: planName }),
      icon: cilCreditCard,
    }
  }

  const openWebStripeCheckout = () => {
    if (typeof window === 'undefined' || !WEB_STRIPE_PAYMENT_URL) {
      return false
    }
    window.open(WEB_STRIPE_PAYMENT_URL, '_blank', 'noopener,noreferrer')
    return true
  }

  const handlePaywallPurchase = async (membership) => {
    if (!paywallSupported || !RevenueCatUI) {
      setPurchaseAlert({
        color: 'warning',
        message: t('payments.messages.paywallUnsupported'),
      })
      logPaymentDebug('paywall:unsupported', { platform: detectedPlatform })
      return false
    }

    if (!revenueCatReady) {
      setPurchaseAlert({
        color: 'warning',
        message: t('payments.messages.paywallNotReady'),
      })
      logPaymentDebug('paywall:not-ready', { platform: detectedPlatform })
      return false
    }

    const entitlementIdentifier = resolveEntitlementIdentifier(membership)
    const offeringIdentifier = resolveOfferingIdentifier(membership)
    console.log('PAYMENT OFFERING identifier: ' + offeringIdentifier)

    let offering
    if (offeringIdentifier) {
      offering = await fetchOfferingByIdentifier(offeringIdentifier)
    }

    const paywallOptions = offering ? { offering } : {}
    const requiredPayload = {
      ...paywallOptions,
      requiredEntitlementIdentifier: entitlementIdentifier || DEFAULT_ENTITLEMENT,
    }

    try {
      const presentIfNeededResponse = await RevenueCatUI.presentPaywallIfNeeded(requiredPayload)
      let paywallResult = resolvePaywallResultValue(presentIfNeededResponse)

      const shouldShowExplicitPaywall =
        paywallResult === undefined || paywallResult === PAYWALL_RESULT.NOT_PRESENTED

      if (shouldShowExplicitPaywall) {
        const paywallResponse = await RevenueCatUI.presentPaywall(paywallOptions)
        paywallResult = resolvePaywallResultValue(paywallResponse)
      }

      if (shouldMarkPaywallSuccess(paywallResult)) {
        logPaymentDebug('paywall:success', {
          paywallResult,
          entitlementIdentifier,
          platform: detectedPlatform,
        })
        const customerInfo = await getRevenueCatCustomerInfo()
        let membershipName = applyRevenueCatEntitlements(customerInfo)
        if (!membershipName) {
          membershipName = membership?.name || membership?.membership_name || 'PRO'
        }
        if (membershipName) {
          setCurrentMembership(membershipName)
          persistMembershipInStorage(membershipName)
          updateAccountMembership(membershipName)
          logPaymentDebug('membership:update', {
            source: 'paywall',
            membershipName,
            platform: detectedPlatform,
          })
          console.log('[Payments] membership:update', {
            source: 'paywall',
            membership: membershipName,
          })
        }
        setPurchaseAlert({
          color: 'success',
          message: t('payments.messages.purchaseSuccess', {
            plan: membership?.name || membership?.membership_name || 'PRO',
          }),
        })
        return true
      }

      if (shouldMarkPaywallFailure(paywallResult)) {
        logPaymentDebug('paywall:cancelled', {
          paywallResult,
          entitlementIdentifier,
        })
        setPurchaseAlert({
          color: paywallResult === PAYWALL_RESULT.ERROR ? 'danger' : 'info',
          message:
            paywallResult === PAYWALL_RESULT.ERROR
              ? t('payments.messages.paywallFailed')
              : t('payments.messages.purchaseCancelled'),
        })
        if (paywallResult === PAYWALL_RESULT.CANCELLED) {
          handleMembershipCancellation('paywall')
        }
        return false
      }

      setPurchaseAlert({
        color: 'warning',
        message: t('payments.messages.paywallNotPresented'),
      })
      logPaymentDebug('paywall:not-presented', {
        paywallResult,
        entitlementIdentifier,
      })
      return false
    } catch (error) {
      console.error('RevenueCat paywall flow failed', error)
      logPaymentDebug('paywall:error', {
        errorMessage: error?.message,
      })
      setPurchaseAlert({
        color: 'danger',
        message: t('payments.messages.paywallFailed'),
      })
      return false
    }
  }

  const handlePayment = async (membership, method) => {
    if (!membership) {
      logPaymentDebug('purchase:missing-membership', {
        method,
        platform: detectedPlatform,
      })
      return
    }

    const membershipLabel = membership.membership_name || membership.name || 'PRO'
    const membershipId = membership?.membership_id ?? null

    logPaymentDebug('purchase:start', {
      method,
      membershipId,
      membershipName: membershipLabel,
      platform: detectedPlatform,
      currentMembership,
    })

    setPurchaseAlert(null)

    if (method === 'PAYWALL') {
      const success = await handlePaywallPurchase(membership)
      if (success) {
        const membershipName = membership?.name || membership?.membership_name || 'PRO'
        setPurchaseAlert({
          color: 'success',
          message: t('payments.messages.purchaseSuccess', { plan: membershipName }),
        })
      }
      return
    }

    if (method === 'CARD') {
      if (detectedPlatform === 'web' && openWebStripeCheckout()) {
        logPaymentDebug('purchase:web-stripe-link', {
          membershipId,
          membershipName: membershipLabel,
          platform: detectedPlatform,
          url: WEB_STRIPE_PAYMENT_URL,
        })
        return
      }
      logPaymentDebug('purchase:card-redirect', {
        membershipId,
        membershipName: membershipLabel,
        platform: detectedPlatform,
      })
      initiateCheckout(membership, method)
      return
    }

    const configured =
      revenueCatReady || (await ensureRevenueCatConfigured({ appUserId: getRevenueCatUserId() }))

    if (!configured) {
      logPaymentDebug('purchase:native-unavailable', {
        method,
        membershipId,
        membershipName: membershipLabel,
        platform: detectedPlatform,
      })
      setRevenueCatError('')
      initiateCheckout(membership, method)
      return
    }

    setRevenueCatReady(true)
    setRevenueCatError('')

    const productIdentifier = resolveProductIdentifier(membership)
    if (!productIdentifier) {
      console.warn('Missing product identifier for membership', membership)
      logPaymentDebug('purchase:missing-product', {
        method,
        membershipId,
        membershipName: membershipLabel,
        platform: detectedPlatform,
      })
      setPurchaseAlert({
        color: 'warning',
        message: t('payments.messages.missingProduct'),
      })
      initiateCheckout(membership, method)
      return
    }

    if (!Purchases) {
      console.warn('RevenueCat SDK is not available on this platform')
      logPaymentDebug('purchase:sdk-unavailable', {
        method,
        membershipId,
        membershipName: membershipLabel,
        platform: detectedPlatform,
      })
      setPurchaseAlert({
        color: 'warning',
        message: t('payments.messages.nativeUnavailable'),
      })
      initiateCheckout(membership, method)
      return
    }

    try {
      const offeringIdentifier = resolveOfferingIdentifier(membership)
      const packageIdentifier = resolvePackageIdentifier(membership)
      const membershipProcessingId = membership?.membership_id ?? productIdentifier
      setPurchaseInProgress(membershipProcessingId)
      logPaymentDebug('purchase:attempt', {
        method,
        membershipId,
        membershipName: membershipLabel,
        platform: detectedPlatform,
        productIdentifier,
        offeringIdentifier,
        packageIdentifier,
      })
      const purchaseResult = await purchaseMembershipWithRevenueCat({
        productIdentifier,
        offeringIdentifier,
        packageIdentifier,
      })
      const customerInfo = purchaseResult?.customerInfo || (await getRevenueCatCustomerInfo())
      let membershipName = applyRevenueCatEntitlements(customerInfo)
      if (!membershipName) {
        membershipName = membership?.name || membership?.membership_name || 'PRO'
      }
      if (membershipName) {
        setCurrentMembership(membershipName)
        persistMembershipInStorage(membershipName)
        updateAccountMembership(membershipName)
        logPaymentDebug('membership:update', {
          source: 'purchase',
          membershipName,
          platform: detectedPlatform,
        })
        console.log('[Payments] membership:update', {
          source: 'purchase',
          membership: membershipName,
        })
      }

      setPurchaseAlert({
        color: 'success',
        message: t('payments.messages.purchaseSuccess', {
          plan: membership.name || membership.membership_name || 'PRO',
        }),
      })
      logPaymentDebug('purchase:success', {
        method,
        membershipId,
        membershipName: membershipLabel,
        platform: detectedPlatform,
        productIdentifier,
      })

      initiateCheckout(membership, method)
    } catch (error) {
      const cancellationCode =
        Purchases?.PURCHASES_ERROR_CODE?.PURCHASE_CANCELLED_ERROR || 'PURCHASE_CANCELLED_ERROR'
      if (error?.code === cancellationCode) {
        console.info('Purchase cancelled by user', error)
        logPaymentDebug('purchase:cancelled', {
          method,
          membershipId,
          membershipName: membershipLabel,
          platform: detectedPlatform,
          productIdentifier,
          errorMessage: error?.message,
        })
        setPurchaseAlert({
          color: 'info',
          message: t('payments.messages.purchaseCancelled'),
        })
        handleMembershipCancellation('purchase')
        return
      }
      console.error('RevenueCat purchase failed', error)
      logPaymentDebug('purchase:error', {
        method,
        membershipId,
        membershipName: membershipLabel,
        platform: detectedPlatform,
        productIdentifier,
        errorMessage: error?.message,
        errorCode: error?.code,
      })
      setPurchaseAlert({
        color: 'danger',
        message: t('payments.messages.purchaseFailed'),
      })
    } finally {
      setPurchaseInProgress(null)
      logPaymentDebug('purchase:complete', {
        method,
        membershipId,
        membershipName: membershipLabel,
        platform: detectedPlatform,
      })
    }
  }

  return (
    <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader
          title={t('payments.header.title')}
          onBack={() => navigate('/dashboard')}
        />

        <CRow className="justify-content-center g-0 mt-2">
          <CCol lg={10} className="d-flex flex-column gap-4">
            <CCard className="border-0 shadow-sm">
              <CCardBody className="d-flex flex-column gap-0">
                {revenueCatError && (
                  <CAlert color="warning" className="mb-3">
                    {revenueCatError}
                  </CAlert>
                )}
                {purchaseAlert && (
                  <CAlert
                    color={purchaseAlert.color}
                    className="mb-3"
                    dismissible
                    onClose={() => setPurchaseAlert(null)}
                  >
                    {purchaseAlert.message}
                  </CAlert>
                )}
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold mb-2">
                    {t('payments.plans.title')}
                  </h6>
                </div>
                <div className="table-responsive d-none d-sm-block">
                  <CTable align="middle" className="mb-0" hover>
                    <CTableHead color="light">
                      <CTableRow>
                        <CTableHeaderCell></CTableHeaderCell>
                        <CTableHeaderCell>{t('payments.table.headers.functions')}</CTableHeaderCell>
                        <CTableHeaderCell className="text-center">
                          {t('payments.table.headers.savings')}
                        </CTableHeaderCell>
                        <CTableHeaderCell className="text-center">
                          {t('payments.table.headers.membership')}
                        </CTableHeaderCell>
                        <CTableHeaderCell className="text-center">
                          {t('payments.table.headers.price')}
                        </CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {memberships.map((membership) => {
                        const normalizedMembershipName = normalizeMembershipName(
                          membership.membership_name || membership.name,
                        )
                        const isCurrentMembership =
                          normalizedMembershipName &&
                          normalizedMembershipName === normalizedCurrentMembership
                        const isFreePlan = normalizedMembershipName === 'FREE'
                        const showUnifiedPaymentButton =
                          !isFreePlan && (paywallSupported || isWebPaySupported)
                        const membershipProcessingId =
                          membership?.membership_id ?? resolveProductIdentifier(membership)
                        const isProcessing = purchaseInProgress === membershipProcessingId
                        const nativeButtonsDisabled = !revenueCatReady || isProcessing
                        const paywallButtonContent = getPaywallButtonContent(membership)
                        const unifiedPaymentMethod = paywallSupported ? 'PAYWALL' : 'CARD'
                        const unifiedButtonDisabled = paywallSupported
                          ? nativeButtonsDisabled
                          : false
                        return (
                          <CTableRow
                            key={membership.membership_id}
                            className={isCurrentMembership ? 'table-primary' : undefined}
                          >
                            <CTableDataCell>
                              <CImage
                                rounded
                                orientation="top"
                                src={membership.pic}
                                width={50}
                                height={50}
                                alt={`${membership.membership_name} plan`}
                              />
                            </CTableDataCell>
                            <CTableDataCell>
                              <div className="fw-semibold">{membership.name}</div>
                              <div className="small text-medium-emphasis">
                                <span>{membership.description}</span>
                              </div>
                            </CTableDataCell>
                            <CTableDataCell className="text-center">
                              <div className="small text-medium-emphasis">{membership.Savings}</div>
                            </CTableDataCell>
                            <CTableDataCell className="text-center">
                              <div className="d-inline-flex align-items-center justify-content-center gap-2">
                                <span
                                  className={
                                    isCurrentMembership
                                      ? 'fw-semibold text-primary'
                                      : 'small text-medium-emphasis'
                                  }
                                >
                                  {membership.membership_name}
                                </span>
                                {isCurrentMembership && (
                                  <span className="badge bg-light text-primary fw-semibold">
                                    {t('payments.labels.currentBadge')}
                                  </span>
                                )}
                              </div>
                            </CTableDataCell>
                            <CTableDataCell className="text-center">
                              <div className="d-flex flex-column align-items-center gap-2">
                                <div className="fw-semibold text-primary">{membership.price}</div>
                                {showUnifiedPaymentButton && (
                                  <div className="d-flex flex-column align-items-stretch gap-2 w-100">
                                    <CButton
                                      color="dark"
                                      size="sm"
                                      className="d-inline-flex justify-content-center align-items-center gap-2"
                                      disabled={unifiedButtonDisabled}
                                      onClick={() =>
                                        handlePayment(membership, unifiedPaymentMethod)
                                      }
                                      aria-label={paywallButtonContent.ariaLabel}
                                    >
                                      {isProcessing ? (
                                        <CSpinner size="sm" color="light" />
                                      ) : (
                                        <CIcon icon={paywallButtonContent.icon} size="lg" />
                                      )}
                                      <span>{paywallButtonContent.label}</span>
                                    </CButton>
                                  </div>
                                )}
                              </div>
                            </CTableDataCell>
                          </CTableRow>
                        )
                      })}
                    </CTableBody>
                  </CTable>
                </div>
                <div className="d-block d-sm-none">
                  <div className="d-flex flex-column gap-3">
                    {memberships.map((membership) => {
                      const normalizedMembershipName = normalizeMembershipName(
                        membership.membership_name || membership.name,
                      )
                      const isCurrentMembership =
                        normalizedMembershipName &&
                        normalizedMembershipName === normalizedCurrentMembership
                      const isFreePlan = normalizedMembershipName === 'FREE'
                      const showUnifiedPaymentButton =
                        !isFreePlan && (paywallSupported || isWebPaySupported)
                      const membershipProcessingId =
                        membership?.membership_id ?? resolveProductIdentifier(membership)
                      const isProcessing = purchaseInProgress === membershipProcessingId
                      const nativeButtonsDisabled = !revenueCatReady || isProcessing
                      const paywallButtonContent = getPaywallButtonContent(membership)
                      const unifiedPaymentMethod = paywallSupported ? 'PAYWALL' : 'CARD'
                      const unifiedButtonDisabled = paywallSupported ? nativeButtonsDisabled : false
                      return (
                        <CCard key={`xs-${membership.membership_id}`} className="border shadow-sm">
                          <CCardBody className="d-flex gap-3">
                            <div>
                              <CImage
                                rounded
                                orientation="top"
                                src={membership.pic}
                                width={48}
                                height={48}
                                alt={`${membership.membership_name} plan`}
                              />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex flex-column gap-1">
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                  <div className="d-inline-flex align-items-center gap-2">
                                    <span className="fw-semibold">{membership.name}</span>
                                    {isCurrentMembership && (
                                      <span className="badge bg-primary-subtle text-primary fw-semibold">
                                        {t('payments.labels.currentBadge')}
                                      </span>
                                    )}
                                  </div>
                                  <CButton
                                    color="secondary"
                                    variant="ghost"
                                    size="sm"
                                    className="p-1 lh-1"
                                    onClick={() => setDetailMembership(membership)}
                                    aria-label={t('payments.cards.showDetails', {
                                      name: membership.name,
                                    })}
                                  >
                                    ?
                                  </CButton>
                                </div>
                                <div
                                  className="small text-medium-emphasis"
                                  style={descriptionClampStyle}
                                >
                                  {membership.description}
                                </div>
                                <div className="d-flex justify-content-between text-nowrap small">
                                  <span>{t('payments.cards.labels.membership')}</span>
                                  <span
                                    className={
                                      isCurrentMembership ? 'fw-semibold text-primary' : undefined
                                    }
                                  >
                                    {membership.membership_name}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between text-nowrap small">
                                  <span>{t('payments.cards.labels.savings')}</span>
                                  <span>{membership.Savings}</span>
                                </div>
                                <div className="d-flex justify-content-between text-nowrap small">
                                  <span>{t('payments.cards.labels.monthly')}</span>
                                  <span className="fw-semibold text-primary">
                                    {membership.price}
                                  </span>
                                </div>
                                {showUnifiedPaymentButton && (
                                  <div className="d-flex flex-column align-items-stretch gap-2 mt-2">
                                    <CButton
                                      color="dark"
                                      size="sm"
                                      className="d-inline-flex align-items-center gap-2"
                                      disabled={unifiedButtonDisabled}
                                      onClick={() =>
                                        handlePayment(membership, unifiedPaymentMethod)
                                      }
                                      aria-label={paywallButtonContent.ariaLabel}
                                    >
                                      {isProcessing ? (
                                        <CSpinner size="sm" color="light" />
                                      ) : (
                                        <CIcon icon={paywallButtonContent.icon} size="lg" />
                                      )}
                                      <span>{paywallButtonContent.label}</span>
                                    </CButton>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CCardBody>
                        </CCard>
                      )
                    })}
                  </div>
                </div>
                <p className="small text-medium-emphasis mb-0">
                  {t('payments.disclaimer.annualFee')}
                </p>
              </CCardBody>
            </CCard>

            <CCard className="border-0 shadow-sm">
              <CCardBody className="d-flex flex-column gap-3">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold mb-2">
                    {t('payments.account.title')}
                  </h6>
                </div>
                {accounts.length > 0 ? (
                  accounts.map((account) => {
                    const isActive = String(account.membership) === currentMembership
                    return (
                      <div
                        key={account.id}
                        className={`p-3 rounded border d-flex flex-column gap-2 ${
                          isActive ? 'bg-primary text-white' : 'bg-light'
                        }`}
                      >
                        <div className="d-flex flex-wrap justify-content-between gap-2">
                          <span className="fw-semibold">
                            {t('payments.account.membershipLabel', {
                              membership: account.membership,
                            })}
                          </span>
                          <span className={isActive ? 'text-white-50' : 'text-medium-emphasis'}>
                            {t('payments.account.validTill', {
                              date: account.validity,
                            })}
                          </span>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <CAlert color="info" className="mb-0">
                    {t('payments.account.loading')}
                  </CAlert>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
      <CModal
        alignment="center"
        visible={Boolean(detailMembership)}
        onClose={() => setDetailMembership(null)}
      >
        <CModalHeader closeButton>
          <CModalTitle>{detailMembership?.name || t('payments.modal.titleFallback')}</CModalTitle>
        </CModalHeader>
        <CModalBody className="d-flex flex-column gap-3">
          {detailMembership && (
            <>
              <div className="d-flex align-items-center gap-3">
                <CImage
                  rounded
                  orientation="top"
                  src={detailMembership.pic}
                  width={64}
                  height={64}
                  alt={`${detailMembership.membership_name} plan`}
                />
                <div>
                  <div className="text-medium-emphasis">{detailMembership.membership_name}</div>
                  <div className="fw-semibold text-primary">
                    {t('payments.modal.price', {
                      price: detailMembership.price,
                    })}
                  </div>
                </div>
              </div>
              <p className="mb-0">{detailMembership.description}</p>
              <div className="small text-medium-emphasis">
                <div>
                  {t('payments.modal.savings', {
                    value: detailMembership.Savings,
                  })}
                </div>
                <div>{t('payments.modal.annualFee')}</div>
              </div>
            </>
          )}
        </CModalBody>
      </CModal>
    </div>
  )
}

export default Payments
